import { Field, Input, Label } from "@headlessui/react";
import type { ApiClientResponse } from "@lobby/api-client";
import { Lobby, Player, SocialNetwork } from "@lobby/core/src/entities";
import { APIError, Button, Icon, useErrorTranslate } from "@lobby/core/src/shared";
import { useTranslate } from "@lobby/ocb-intl";
import { Link, useLocation } from "@tanstack/react-router";
import { clsx } from "clsx";
import { SocialNetworkLink } from "features/social-network-link";
import { useState } from "react";
import { AuthEndpointsContainer, Separator } from "./components";

interface ILoginFormProps {
  className?: string;
}

export function LoginForm({ className }: ILoginFormProps) {
  // const [email, setEmail] = useState("");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [validationError, setValidationError] = useState("");

  const { $t } = useTranslate();
  const { formatMessage: translateError } = useErrorTranslate();
  const location = useLocation();

  const { data: lobbyData } = Lobby.useLobby();
  const { mutate: loginMutate, isPending } = Player.useLogIn();
  const { data: authEndpointsData } = SocialNetwork.useAuthEndpoints();

  const authEndpoints = authEndpointsData?.result?.data ?? [];
  const isRegistrationEnabled = lobbyData?.registration.enabled ?? false;

  function handleSubmit(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault();

    setValidationError("");

    const form = new FormData(ev.currentTarget);

    if (validateForm(form)) {
      logIn(form);
    }
  }

  function logIn(form: FormData) {
    loginMutate(
      {
        login: form.get("login") as string,
        password: form.get("password") as string,
      },
      { onSuccess: handleLogInSuccess },
    );
  }

  function handleLogInSuccess(data: ApiClientResponse<"Player.logIn">) {
    const error = data.error;
    if (error) {
      const message = translateError(new APIError(error.message, { code: error.code }));
      setValidationError(message);
    }
  }

  function validateForm(form: FormData) {
    return form.has("login") && form.has("password");
  }

  return (
    <div className={clsx("text-14 lg:text-16", className)}>
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* <Field className="flex flex-col">
          <Label className="font-bold">{$t({ defaultMessage: "E-mail" })}:</Label>
          <Input
            className="input border-shark/80 border-1"
            name="email"
            type="email"
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            autoComplete="email"
            autoCapitalize="none"
            minLength={3}
            maxLength={255}
            autoFocus
            required
          />
        </Field> */}

        <Field className="flex flex-col">
          <Label>{$t({ defaultMessage: "Login" })}:</Label>
          <Input
            name="login"
            type="text"
            value={login}
            onChange={(ev) => setLogin(ev.target.value)}
            minLength={3}
            maxLength={255}
            autoCapitalize="none"
            autoFocus
            required
          />
        </Field>

        <Field className="flex flex-col">
          <Label>{$t({ defaultMessage: "Password" })}:</Label>
          <div className="relative">
            <Input
              className="w-full pe-13"
              name="password"
              type={isPasswordVisible ? "text" : "password"}
              value={password}
              onChange={(ev) => setPassword(ev.target.value)}
              autoComplete="current-password"
              minLength={6}
              maxLength={255}
              required
            />
            <div
              className="-translate-y-1/2 absolute top-1/2 ltr:right-0 rtl:left-0 p-3 text-20 text-white hover:cursor-pointer hover:text-white/80"
              onClick={() => setIsPasswordVisible((state) => !state)}
            >
              {isPasswordVisible ? <Icon name="openedEye" /> : <Icon name="closedEye" />}
            </div>
          </div>
        </Field>

        {validationError && (
          <p className="text-center text-alizarin-crimson leading-none">{validationError}</p>
        )}

        {isRegistrationEnabled && (
          <div className="text-center">
            <Link to={location.pathname} search={{ modal: "password-forgot" }} replace>
              {$t({ defaultMessage: "Forgot password?" })}
            </Link>
          </div>
        )}

        <Button variant="secondary" type="submit" loading={isPending} className="mx-auto">
          {$t({ defaultMessage: "Log In" })}
        </Button>
      </form>

      {authEndpoints.length > 0 && (
        <>
          <Separator>{$t({ defaultMessage: "or" })}</Separator>
          <AuthEndpointsContainer>
            {authEndpoints.map(({ name, stringId, url }) => (
              <SocialNetworkLink key={stringId} name={name} stringId={stringId} url={url} />
            ))}
          </AuthEndpointsContainer>
        </>
      )}
    </div>
  );
}
